import axios from '@axios'

/**
 * Sequencial de requisições do `fetchActivePromotions`. Usado para descartar
 * respostas obsoletas em situações de race: várias chamadas concorrentes
 * (bootstrap → mounted → loadSale → setClientForm) podem terminar fora de
 * ordem em ambientes mais rápidos (ex.: staging) e fazer com que uma resposta
 * antiga sobrescreva o cache mais recente. Apenas a chamada cujo
 * `requestId === lastRequestId` no momento da resposta tem permissão para
 * commitar o resultado.
 */
let lastRequestId = 0

const getInitialState = () => ({
  /**
   * Promoções `SpendAndGet` ativas/vigentes da loja atual.
   * Cada item segue o contrato `PayBoxSpendAndGetPromotionDto`:
   *   { id, name, description, imageSquare, imageWide, startDate, endDate,
   *     tiers: [{ id, minimumAmount, rewards: [{ id, skuId, quantity, sku }] }] }
   */
  activePromotions: [],
  storeIdLoaded: null,
  /**
   * Tabela de preço usada na última busca. O backend filtra a lista por
   * `PriceTable.ActiveOnline` quando `priceTableId` é informado: para tabelas
   * não-Online, a resposta é vazia. Mantemos essa chave para refetch automático
   * quando a tabela da venda muda.
   *
   * NOTA: a regra é orientada à tabela da venda em curso, não à tabela do
   * cliente. Uma venda pode ser feita em uma tabela diferente da do cadastro
   * (ex.: cliente B2B operando no caixa em tabela de varejo).
   */
  priceTableIdLoaded: null,
  loading: false,
  loadedAt: null,
})

export default {
  namespaced: true,

  state: getInitialState(),

  getters: {
    /** Mapa `promotionId -> promotion` para lookup O(1) no reprocessador. */
    promotionsById: state =>
      state.activePromotions.reduce((acc, p) => {
        acc[p.id] = p
        return acc
      }, {}),

    /** Conjunto de ids de promoções SpendAndGet ativas, usado pelo
     *  `spendAndGetUtils.calculateEligibleAmount` e `isSpendAndGetReward`. */
    activePromotionIds: state => new Set(state.activePromotions.map(p => p.id)),

    /** Verdadeiro quando há ao menos uma promoção SpendAndGet vigente. */
    hasActivePromotions: state => state.activePromotions.length > 0,
  },

  mutations: {
    SET_ACTIVE_PROMOTIONS(state, { promotions, storeId, priceTableId }) {
      // eslint-disable-next-line no-console
      console.log('[SAG][SET_ACTIVE_PROMOTIONS]', {
        count: promotions?.length ?? 0,
        storeId,
        priceTableId,
        ids: (promotions || []).map(p => p.id),
      })
      state.activePromotions = promotions || []
      state.storeIdLoaded = storeId
      state.priceTableIdLoaded = priceTableId ?? null
      state.loadedAt = Date.now()
    },
    SET_LOADING(state, value) {
      state.loading = value
    },
    CLEAN_STATE(state) {
      lastRequestId += 1
      Object.assign(state, getInitialState())
    },
  },

  actions: {
    /**
     * Busca as promoções `SpendAndGet` ativas/vigentes da loja informada.
     *
     * Quando `priceTableId` é informado, o backend valida se a tabela tem
     * `ActiveOnline = true` e devolve lista vazia caso contrário. Isso evita
     * exibir SpendAndGet quando a venda corre em tabela não-Online (alinhado
     * ao `PromotionService.GetActiveSpendAndGetPromotionsByStoreAsync`).
     *
     * Importante: a regra é baseada apenas na tabela DA VENDA (a tabela do
     * cliente NÃO é usada como filtro). O caller deve passar
     * `state.priceTable?.id`.
     *
     * Faz cache em memória e só refaz a chamada quando `storeId` ou
     * `priceTableId` mudam (ou quando `force = true`). Resposta vazia ainda
     * é cacheada para evitar refetch.
     *
     * Race-safety: cada chamada incrementa um contador global; respostas
     * cuja chamada não é mais a "mais recente" são descartadas. Isso evita
     * que uma resposta lenta de uma chamada antiga sobrescreva o resultado
     * de uma chamada posterior (cenário comum entre bootstrap → loadSale).
     */
    async fetchActivePromotions(
      { commit, state, rootGetters },
      { storeId, priceTableId = null, force = false } = {}
    ) {
      // Vendas associadas a evento não participam de promoções SpendAndGet.
      // A limpeza também invalida requisições em andamento, impedindo que uma
      // resposta antiga volte a popular o cache e apareça no ATM do evento.
      const eventId = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']?.eventId
      if (eventId != null) {
        commit('CLEAN_STATE')
        return []
      }

      if (!storeId) {
        commit('CLEAN_STATE')
        return []
      }

      const sameKey = state.storeIdLoaded === storeId
        && state.priceTableIdLoaded === (priceTableId ?? null)

      if (!force && sameKey) {
        // eslint-disable-next-line no-console
        console.log('[SAG][fetchActivePromotions] CACHE HIT', { storeId, priceTableId, count: state.activePromotions.length })
        return state.activePromotions
      }

      lastRequestId += 1
      const requestId = lastRequestId
      // eslint-disable-next-line no-console
      console.log('[SAG][fetchActivePromotions] START', { requestId, storeId, priceTableId, force })

      try {
        commit('SET_LOADING', true)
        const params = {}
        if (priceTableId != null) params.priceTableId = priceTableId

        const { data } = await axios.get(
          `/api/sales/pay-box-sku/store/${storeId}/spend-and-get-promotions`,
          { params }
        )

        if (requestId !== lastRequestId) {
          // eslint-disable-next-line no-console
          console.warn('[SAG][fetchActivePromotions] DISCARDED stale response', { requestId, lastRequestId, count: data?.length })
          // Outra chamada foi disparada depois desta — descartamos a resposta
          // para preservar o resultado mais recente.
          return state.activePromotions
        }

        // eslint-disable-next-line no-console
        console.log('[SAG][fetchActivePromotions] OK', { requestId, count: data?.length, priceTableId })
        commit('SET_ACTIVE_PROMOTIONS', {
          promotions: data || [], storeId, priceTableId,
        })
        return data || []
      } catch (err) {
        // Não bloqueia o PDV se o endpoint falhar; logar e seguir sem brindes.
        // eslint-disable-next-line no-console
        console.warn('[spendAndGetPromotions] fetch failed:', err?.message || err)
        if (requestId !== lastRequestId) {
          return state.activePromotions
        }
        commit('SET_ACTIVE_PROMOTIONS', {
          promotions: [], storeId, priceTableId,
        })
        return []
      } finally {
        if (requestId === lastRequestId) {
          commit('SET_LOADING', false)
        }
      }
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
